import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { getKnockout } from '../api';
import PageHeader from '../components/PageHeader';
import KnockoutDriftChart from '../components/KnockoutDriftChart';
import KnockoutBracket from '../components/KnockoutBracket';

const BASIS_LABEL: Record<string, string> = {
  'pre-tournament': 'Pre-tournament — based on bookmaker 1 X 2 odds',
  'in-progress': 'Group stage in progress — blending actual results + odds',
  'post-group': 'Group stage complete — based on actual standings + odds',
};

export default function Knockout() {
  const { data, isLoading } = useQuery({ queryKey: ['knockout'], queryFn: getKnockout });

  if (isLoading) return <PageHeader title="Knockout predictor" />;
  if (!data) return null;

  return (
    <>
      <PageHeader
        title="Knockout"
        subtitle={BASIS_LABEL[data.basis] ?? ''}
      />
      <div className="px-4 lg:px-0 space-y-4">
        <KnockoutBracket />
        <h2 className="text-xs uppercase tracking-widest text-dim pt-2">Cup predictor</h2>
        <FavoritesPodium teams={data.teams.slice(0, 3)} />
        <KnockoutDriftChart />
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-dim border-b border-ink-800">
                <th className="text-left py-2 px-3 font-medium w-8">#</th>
                <th className="text-left font-medium">Team</th>
                <th className="text-center font-medium w-16">Strength</th>
                <th className="text-center font-medium w-14">SF</th>
                <th className="text-center font-medium w-14">Final</th>
                <th className="text-center font-medium w-14 pr-3">Cup</th>
              </tr>
            </thead>
            <tbody>
              {data.teams.map((t, i) => (
                <tr key={t.code} className="border-b border-ink-800/40 last:border-0">
                  <td className="py-2 px-3 stat text-dim">{i + 1}</td>
                  <td className="py-2">
                    <div className="flex items-center gap-1.5 min-w-0">
                      <Link to={`/teams/${t.code}`} className="flex items-center gap-1.5 min-w-0 hover:text-pitch-400 transition-colors">
                        <span className="text-base leading-none">{t.flag}</span>
                        <span className="text-xs truncate">{t.name}</span>
                      </Link>
                      {t.owner && (
                        <Link to={`/players/${encodeURIComponent(t.owner)}`} className="text-[9px] uppercase tracking-wider text-dim hover:text-pitch-400">
                          {t.owner}
                        </Link>
                      )}
                    </div>
                  </td>
                  <td className="text-center stat text-xs">{t.strength.toFixed(2)}</td>
                  <td className="text-center stat text-xs">{pct(t.semiChance)}</td>
                  <td className="text-center stat text-xs">{pct(t.finalChance)}</td>
                  <td className="text-center stat text-xs pr-3 text-pitch-400 font-semibold">{pct(t.cupChance)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-dim max-w-prose mx-auto text-center">
          Strength = (actual pts so far + expected remaining group pts) / 9. Cup / Final / SF probabilities are softmax over strengths. Not a bracket simulation — pure favorite-vs-field model.
        </p>
      </div>
    </>
  );
}

function FavoritesPodium({ teams }: { teams: NonNullable<Awaited<ReturnType<typeof getKnockout>>>['teams'] }) {
  if (teams.length === 0) return null;
  return (
    <div className="grid grid-cols-3 gap-2">
      {teams.map((t, i) => (
        <Link key={t.code} to={`/teams/${t.code}`} className={`card p-3 text-center hover:border-pitch-500/40 transition-colors ${i === 0 ? 'ring-1 ring-pitch-500/40' : ''}`}>
          <div className="text-3xl leading-none mb-1">{t.flag}</div>
          <div className="text-xs font-semibold truncate">{t.name}</div>
          {t.owner && <div className="text-[10px] text-dim">{t.owner}</div>}
          <div className="stat text-pitch-400 text-base mt-1">{pct(t.cupChance)}</div>
          <div className="text-[10px] uppercase tracking-wider text-dim">cup chance</div>
        </Link>
      ))}
    </div>
  );
}

function pct(v: number): string {
  if (!v || !Number.isFinite(v)) return '—';
  if (v < 0.001) return '<0.1%';
  return `${(v * 100).toFixed(1)}%`;
}
